import { useEffect } from "react";

import { useFirebaseAuth } from "../user";
import { useBoardTypeFirestorePersistence } from "./use-board-type-firestore-persistence";
import { useBoardTypeFirestoreSWR } from "./use-board-type-firestore-swr";
import { useBoardTypeLocalPersistence } from "./use-board-type-local-persistence";
import { useBoardTypeLocalSWR } from "./use-board-type-local-swr";

/**
 * Reads are local, so the Firestore board type reaches this device only here, and it
 * wins wholesale — Firestore is the authority, and the local copy is a device-level
 * store that may well belong to whoever signed in last. The device's selection is
 * pushed up only when Firestore holds none, which is what carries a board type picked
 * while anonymous into Firestore on a first sign-in.
 */
export function useBoardTypeSyncEffect(): void {
  const { userId, authenticated } = useFirebaseAuth();
  const { data: firestoreBoardType, mutate: mutateFirestore } =
    useBoardTypeFirestoreSWR();
  const { data: localBoardType, mutate: mutateLocal } = useBoardTypeLocalSWR();
  const { setBoardType: setLocalBoardType } = useBoardTypeLocalPersistence();
  const { setBoardType: setFirestoreBoardType } =
    useBoardTypeFirestorePersistence();

  useEffect(() => {
    // An anonymous user has no Firestore key, so `firestoreBoardType` is not a resolved
    // read here and must not be mistaken for Firestore holding nothing.
    if (!authenticated) return;

    if (firestoreBoardType !== null) {
      void mutateLocal(
        setLocalBoardType(firestoreBoardType).then(() => firestoreBoardType),
        { optimisticData: firestoreBoardType, revalidate: false },
      );
      return;
    }

    if (localBoardType !== null) {
      void mutateFirestore(
        setFirestoreBoardType(userId, localBoardType).then(
          () => localBoardType,
        ),
        { optimisticData: localBoardType, revalidate: false },
      );
    }
  }, [
    authenticated,
    userId,
    firestoreBoardType,
    localBoardType,
    mutateLocal,
    mutateFirestore,
    setLocalBoardType,
    setFirestoreBoardType,
  ]);
}
